import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from 'components/icons';

/**
 * Shared shell for Terms and Privacy.
 *
 * Carries a visible pre-review notice by design. These were drafted against
 * what the system actually does — the third-party services it calls, the data
 * it stores, the rights already implemented — but they have not been reviewed
 * by a lawyer, and Clevor is not yet an incorporated entity. Publishing them
 * silently as finished legal terms would misrepresent both.
 */
const LegalPage: React.FC<{
  title: string;
  updated: string;
  children: React.ReactNode;
}> = ({ title, updated, children }) => (
  <div className="bg-surface">
    <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24 lg:px-8">
      <h1 className="text-display text-content text-balance">{title}</h1>
      <p className="mt-3 text-sm text-content-muted">Last updated: {updated}</p>

      <div className="mt-8 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
        <ExclamationTriangleIcon
          className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <div className="text-sm text-amber-900">
          <p className="font-semibold">Draft — pending legal review</p>
          <p className="mt-1 text-amber-800">
            Clevor is in private beta and is not yet an incorporated entity. This document
            describes how the service currently works and what we currently commit to, but it has
            not been reviewed by an attorney. It will be replaced before general availability.
            Questions:{' '}
            <a href="mailto:support@clevor.ai" className="underline">
              support@clevor.ai
            </a>
            .
          </p>
        </div>
      </div>

      <div className="mt-10 space-y-8 text-content-muted leading-relaxed [&_h2]:text-title [&_h2]:text-content [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-semibold [&_h3]:text-content [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_p]:mt-3 [&_a]:text-brand-600 [&_a]:underline">
        {children}
      </div>

      <div className="mt-16 pt-8 border-t border-border flex flex-wrap gap-6 text-sm">
        <Link to="/terms" className="text-content-muted hover:text-content">
          Terms of Service
        </Link>
        <Link to="/privacy" className="text-content-muted hover:text-content">
          Privacy Policy
        </Link>
        <Link to="/" className="text-content-muted hover:text-content">
          Back to Clevor
        </Link>
      </div>
    </div>
  </div>
);

export default LegalPage;
